import { useApolloClient, useLazyQuery } from '@apollo/client/react'
import { createLocalStorage } from '@faceup/ui'
import {
  convertRegionToSupportedRegion,
  DEFAULT_REGION,
  type Region,
  SupportedRegion,
} from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useCallback } from 'react'
import { graphql } from '../__generated__'

const query = {
  EmailDiscovery: graphql(`
    query EmailDiscoveryQuery($email: Email!) {
      region: emailDiscovery(email: $email)
    }
  `),
  InstitutionRegionDiscovery: graphql(`
    query InstitutionRegionDiscoveryQuery($institutionId: UUID!) {
      region: institutionRegionDiscovery(institutionId: $institutionId)
    }
  `),
}

const REGION_KEY = 'region'

// Region used when none is selected yet (no localStorage value). Matches what the API falls back to
// when no region header is sent, so the UI shows the region the requests actually hit.
export const DEFAULT_SUPPORTED_REGION = convertRegionToSupportedRegion(DEFAULT_REGION)

const regionSchema = yup.string().oneOf(Object.values(SupportedRegion)).nullable().defined()

// Selected administration region, persisted in localStorage and shared across every useRegion
// consumer (the region select in the header, the region indicator in the navigation, the
// institution detail prompt) so they all re-render together when it changes. kredenc is an
// internal admin tool that can browse any region, so the region is a user-controlled selection
// rather than something derived from the logged-in user.
const useRegionStorage = createLocalStorage(REGION_KEY, regionSchema, null)

// Non-hook reader for the Apollo links, which resolve the region at request time. createLocalStorage
// (mantine) stores the value JSON-encoded; fall back to the raw string for values written by older
// clients.
export const retrieveRegion = (): SupportedRegion | null => {
  const raw = localStorage.getItem(REGION_KEY)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as SupportedRegion
  } catch {
    return raw as SupportedRegion
  }
}

const toSupportedRegion = (region: string | null | undefined): SupportedRegion | null => {
  if (!region) {
    return null
  }

  try {
    return convertRegionToSupportedRegion(region as Region)
  } catch {
    return null
  }
}

export const useRegion = () => {
  const client = useApolloClient()
  const [region, setRegionStorage] = useRegionStorage()

  const [fetchEmailDiscovery, { loading }] = useLazyQuery(query.EmailDiscovery, {
    fetchPolicy: 'cache-and-network',
  })

  // Persist the new region (shared across all consumers) and reset the cache so data is refetched
  // against it.
  const setRegion = useCallback(
    (newRegion: SupportedRegion | null) => {
      if (!newRegion || newRegion === region) {
        return
      }

      setRegionStorage(newRegion)
      client.resetStore()
    },
    [client, region, setRegionStorage]
  )

  const discoverByEmail = async (email: string) => {
    if (region) {
      return null
    }

    const { data } = await fetchEmailDiscovery({ variables: { email } })
    const discovered = toSupportedRegion(data?.region)

    if (!discovered) {
      return null
    }

    setRegion(discovered)

    return discovered
  }

  // Region-agnostic discovery of an institution's region by id (resolved against the global
  // discovery tables). Returns only the region and performs no side effects - the caller decides
  // whether to prompt the user to switch the administration region. It runs during render through
  // usePromiseSuspense, where Apollo 4 forbids a lazy query.
  const discoverByInstitution = async (institutionId: string) => {
    const { data } = await client.query({
      query: query.InstitutionRegionDiscovery,
      variables: { institutionId },
      fetchPolicy: 'cache-first',
    })

    return toSupportedRegion(data?.region)
  }

  return {
    region,
    setRegion,
    discoverByEmail,
    discoverByInstitution,
    loading,
  }
}
